<!-- Page Header -->
<app-page-header title="Yearly Revenue Report" subtitle="Annual revenue analysis and comparison">
    <div actions class="flex items-center gap-2">
        <div class="flex gap-1 bg-gray-100 rounded-lg p-1">
            <p-button
                label="View"
                [text]="true"
                [severity]="viewOrCompare === 0 ? 'info' : 'secondary'"
                (click)="toggleButton(0)"
                styleClass="text-sm px-3 py-1.5"
                [ngClass]="{ 'bg-white shadow-sm rounded-md': viewOrCompare === 0 }"
            ></p-button>
            <p-button
                label="Comparison"
                [text]="true"
                [severity]="viewOrCompare === 1 ? 'info' : 'secondary'"
                (click)="toggleButton(1)"
                styleClass="text-sm px-3 py-1.5"
                [ngClass]="{ 'bg-white shadow-sm rounded-md': viewOrCompare === 1 }"
            ></p-button>
        </div>
        <p-button
            label="Export Report"
            icon="download"
            (click)="exportToExcel()"
            [disabled]="
                loading || (viewOrCompare === 0 && selectedYears.length === 0) || (viewOrCompare === 1 && selectedYears.length !== 2)
            "
        >
        </p-button>
    </div>
</app-page-header>

<!-- Year Selection Filter -->
<app-card icon="event_note" title="Year Selection" iconGradient="from-gray-500 to-gray-600" class="mb-4">
    <div class="flex flex-wrap items-center gap-4">
        @if (viewOrCompare === 0) {
            <div class="flex items-center gap-3">
                <span class="text-sm font-medium text-gray-600">Year(s) Selection</span>
                <p-multiSelect
                    [filter]="false"
                    [options]="years"
                    [(ngModel)]="selectedYears"
                    placeholder="Select Year(s)"
                    [maxSelectedLabels]="5"
                    [selectionLimit]="4"
                />
                <p-button variant="outlined" label="Search" (click)="searchViewBySelectedYears()"></p-button>
            </div>
        }
        @if (viewOrCompare === 1) {
            <div class="flex flex-wrap items-center gap-3">
                <span class="text-sm font-medium text-gray-600">Compare Years</span>
                <p-multiSelect
                    [filter]="false"
                    [options]="years"
                    [(ngModel)]="selectedYears"
                    placeholder="Select Year(s)"
                    [maxSelectedLabels]="5"
                    [selectionLimit]="2"
                />
                <p-button variant="outlined" label="Compare" (click)="searchViewBySelectedYears()"></p-button>
                <p-button link severity="secondary" label="Clear Selections" (click)="resetSelection()"></p-button>
                @if (errorMsg.length > 0) {
                    <small class="text-red-500">{{ errorMsg }}</small>
                }
            </div>
        }
    </div>
</app-card>
<!-- Revenue Breakdown Table -->
<app-card icon="bar_chart" title="Monthly Revenue Breakdown" iconGradient="from-indigo-500 to-purple-600" class="mb-4">
    <ng-template #headerTemplate>
        <div class="ml-auto flex gap-1 bg-gray-100 rounded-lg p-1">
            <p-button
                label="Booking Date"
                [text]="true"
                [severity]="dateBasis === BookingDateOrFlightDate.BookingDate ? 'info' : 'secondary'"
                (click)="setDateBasis(BookingDateOrFlightDate.BookingDate)"
                styleClass="text-sm px-3 py-1"
                [ngClass]="{ 'bg-white shadow-sm rounded-md': dateBasis === BookingDateOrFlightDate.BookingDate }"
            ></p-button>
            <p-button
                label="Flight Date"
                [text]="true"
                [severity]="dateBasis === BookingDateOrFlightDate.FlightDate ? 'info' : 'secondary'"
                (click)="setDateBasis(BookingDateOrFlightDate.FlightDate)"
                styleClass="text-sm px-3 py-1"
                [ngClass]="{ 'bg-white shadow-sm rounded-md': dateBasis === BookingDateOrFlightDate.FlightDate }"
            ></p-button>
        </div>
    </ng-template>

    <p-table
        [loading]="loading"
        [value]="months"
        [scrollable]="true"
        scrollDirection="horizontal"
        styleClass="p-datatable-sm"
        [tableStyle]="{ 'min-width': '800px', 'margin-top': '20px' }"
    >
        <ng-template pTemplate="header">
            <tr>
                <th class="!text-left font-semibold !bg-gray-100">Month</th>
                <ng-container>
                    <ng-container *ngFor="let year of selectedYears; let i = index">
                        <th colspan="4" class="!text-center font-semibold" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                            {{ year }}
                        </th>
                    </ng-container>
                </ng-container>
            </tr>
            <tr>
                <th class="text-left font-medium !bg-gray-100"></th>
                <ng-container>
                    <ng-container *ngFor="let year of selectedYears; let i = index">
                        <th class="!text-center font-medium" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">Revenue</th>
                        <th class="!text-center font-medium" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">Costs</th>
                        <th class="!text-center font-medium" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">Margin</th>
                        <th class="!text-center font-medium" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">Margin(%)</th>
                    </ng-container>
                </ng-container>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-month let-rowIndex="rowIndex">
            <tr>
                <td class="font-medium !bg-gray-100">{{ month }}</td>
                <ng-container>
                    <ng-container *ngFor="let year of selectedYears; let i = index">
                        <td class="!text-center" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                            {{ getMonthlyValue(year, rowIndex + 1, 'revenue') }}
                        </td>
                        <td class="!text-center" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                            {{ getMonthlyValue(year, rowIndex + 1, 'cost') }}
                        </td>
                        <td class="!text-center" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                            {{ getMonthlyValue(year, rowIndex + 1, 'marginAmount') }}
                        </td>
                        <td class="!text-center" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                            {{ getMonthlyValue(year, rowIndex + 1, 'margin') }}
                        </td>
                    </ng-container>
                </ng-container>
            </tr>
        </ng-template>
        <ng-template pTemplate="footer">
            <tr>
                <td class="font-semibold !bg-gray-100">Total</td>
                <ng-container>
                    <ng-container *ngFor="let year of selectedYears; let i = index">
                        <td class="!text-center font-semibold" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                            {{ getTotalValue(year, 'revenue') }}
                        </td>
                        <td class="!text-center font-semibold" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                            {{ getTotalValue(year, 'cost') }}
                        </td>
                        <td class="!text-center font-semibold" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                            {{ getTotalValue(year, 'marginAmount') }}
                        </td>
                        <td class="!text-center font-semibold" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                            {{ getTotalValue(year, 'margin') }}
                        </td>
                    </ng-container>
                </ng-container>
            </tr>
        </ng-template>
    </p-table>
</app-card>

<!-- Reservation Count Table -->
<app-card icon="confirmation_number" title="Reservation Count" iconGradient="from-teal-500 to-green-600" class="mb-4">
    <p-table
        [loading]="loading"
        [value]="selectedYears"
        [scrollable]="true"
        scrollDirection="horizontal"
        styleClass="p-datatable-sm"
        [tableStyle]="{ 'min-width': '800px', 'margin-top': '20px' }"
    >
        <ng-template pTemplate="header">
            <tr>
                <th class="!text-left font-semibold !bg-gray-100">Year</th>
                <ng-container *ngFor="let month of months; let i = index">
                    <th colspan="4" class="!text-center font-semibold" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                        {{ month }}
                    </th>
                </ng-container>
                <th class="!text-center !bg-white">Total</th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-year let-rowIndex="rowIndex">
            <tr>
                <td class="font-medium !bg-gray-100">{{ year }}</td>
                <ng-container *ngFor="let month of months; let i = index">
                    <td colspan="4" class="!text-center" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                        {{ getReservationCount(year, i) }}
                    </td>
                </ng-container>
                <td class="!text-center !bg-white">{{ getTotalReservationCount(year) }}</td>
            </tr>
        </ng-template>
        <ng-template #footer *ngIf="viewOrCompare === 1">
            <tr class="!text-center">
                <td class="!bg-gray-100 font-medium">{{ comparisonData?.year1 }}vs{{ comparisonData?.year2 }}</td>
                <ng-container *ngFor="let month of months; let i = index">
                    <td
                        colspan="4"
                        class="!text-center"
                        [ngClass]="[
                            i % 2 === 0 ? '!bg-white' : '!bg-gray-100',
                            getTextColorByNegativeOrPositive(getMonthlyComparison('reservationCount', i))
                        ]"
                    >
                        {{ getMonthlyComparison('reservationCount', i) }}
                    </td>
                </ng-container>
                <td
                    colspan="4"
                    class="!text-center"
                    [ngClass]="getTextColorByNegativeOrPositive(comparisonData?.yearlyComparison?.passengerCount || 0)"
                >
                    {{ comparisonData?.yearlyComparison?.passengerCount || 0 }}
                </td>
            </tr>
        </ng-template>
    </p-table>
</app-card>
<!-- Passenger Count Table -->
<app-card icon="group" title="Passenger Count" iconGradient="from-orange-500 to-amber-600">
    <p-table
        [loading]="loading"
        [value]="selectedYears"
        [scrollable]="true"
        scrollDirection="horizontal"
        styleClass="p-datatable-sm"
        [tableStyle]="{ 'min-width': '800px', 'margin-top': '20px' }"
    >
        <ng-template pTemplate="header">
            <tr>
                <th class="!text-left font-semibold !bg-gray-100">Year</th>
                <ng-container *ngFor="let month of months; let i = index">
                    <th colspan="4" class="!text-center font-semibold" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                        {{ month }}
                    </th>
                </ng-container>
                <th class="!text-center !bg-white">Total</th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-year let-rowIndex="rowIndex">
            <tr>
                <td class="font-medium !bg-gray-100">{{ year }}</td>
                <ng-container *ngFor="let month of months; let i = index">
                    <td colspan="4" class="!text-center" [ngClass]="i % 2 === 0 ? '!bg-white' : '!bg-gray-100'">
                        {{ getPassengerCount(year, i) }}
                    </td>
                </ng-container>
                <td class="!text-center !bg-white">{{ getTotalPassengerCount(year) }}</td>
            </tr>
        </ng-template>
        <ng-template #footer *ngIf="viewOrCompare === 1">
            <tr class="!text-center">
                <td class="!bg-gray-100 font-medium">{{ comparisonData?.year1 }}vs{{ comparisonData?.year2 }}</td>
                <ng-container *ngFor="let month of months; let i = index">
                    <td
                        colspan="4"
                        class="!text-center"
                        [ngClass]="[
                            i % 2 === 0 ? '!bg-white' : '!bg-gray-100',
                            getTextColorByNegativeOrPositive(getMonthlyComparison('passengerCount', i))
                        ]"
                    >
                        {{ getMonthlyComparison('passengerCount', i) }}
                    </td>
                </ng-container>
                <td
                    colspan="4"
                    class="!text-center"
                    [ngClass]="getTextColorByNegativeOrPositive(comparisonData?.yearlyComparison?.passengerCount || 0)"
                >
                    {{ comparisonData?.yearlyComparison?.passengerCount || 0 }}
                </td>
            </tr>
        </ng-template>
    </p-table>
</app-card>
